import type { Automation, OverrideBehavior } from '@capture/shared';
import { SectionCard } from './SectionCard';
import { RoleScopePicker, roleLabel } from './RoleScopePicker';
import styles from './panel.module.css';

interface Props {
  automation: Automation;
  onChange: (next: Automation) => void;
  /** The tenant's REAL roles — the "Specific roles" picker writes the stable
   *  slug and displays the name (rename-safe, same shape as BranchesSection). */
  roles?: { slug: string; name: string }[];
}

const ON_OVERRIDE_OPTIONS: { value: OverrideBehavior; label: string }[] = [
  { value: 'override', label: 'Continue — the operator proceeds' },
  { value: 'escalate', label: 'Escalate for review' },
  { value: 'block', label: 'Not overridable' },
];

// Legacy rules persist no overrideBehavior; the RUNTIME treats a missing value
// as releasable (it blocks only on an explicit 'block'). The display default
// must match that runtime-effective meaning — "Continue/Overridable", NOT
// "Cannot be overridden" — and we write a value only when the operator picks one.
const DISPLAY_DEFAULT: OverrideBehavior = 'override';

function overrideSummary(automation: Automation, roles: { slug: string; name: string }[]): string {
  const behavior = automation.overrideBehavior ?? DISPLAY_DEFAULT;
  if (behavior === 'override') {
    const restricted = automation.overrideConfig?.overrideRoles ?? [];
    const by = restricted.length
      ? ` by ${restricted.map((t) => roleLabel(t, roles)).join(', ')}`
      : '';
    return automation.overrideConfig?.requireReason
      ? `Overridable${by} with a reason`
      : `Overridable${by}`;
  }
  if (behavior === 'escalate') return 'Escalates on override';
  return 'Cannot be overridden';
}

/**
 * Who may override / require-a-reason / on-override outcome. E-033 — an
 * automation whose every branch is warn-only has no override story (warnings
 * never block accept), so the section doesn't render at all.
 *
 * "Who may override: Specific roles" (finish-promised-runtimes item 1) —
 * writes overrideConfig.overrideRoles (slugs); the server enforces it at
 * POST /documents/:id/override in addition to the step's override permission.
 */
export function OverrideSection({ automation, onChange, roles = [] }: Props) {
  const warnOnly = automation.branches.length > 0 && automation.branches.every((b) => b.severity === 'warning');
  if (warnOnly) return null;

  const behavior: OverrideBehavior = automation.overrideBehavior ?? DISPLAY_DEFAULT;
  const overrideRoles = automation.overrideConfig?.overrideRoles;

  function writeOverrideRoles(next: string[] | undefined) {
    const { overrideRoles: _dropped, ...rest } = automation.overrideConfig ?? { requireReason: false };
    onChange({
      ...automation,
      overrideConfig: next === undefined ? rest : { ...rest, overrideRoles: next },
    });
  }

  return (
    <div data-testid="override-section">
      <SectionCard title="Override" summary={overrideSummary(automation, roles)} defaultOpen>
        <div className={styles.formGrid}>
          <span className={styles.fieldLabel} id="override-who-label">
            Who may override
          </span>
          <RoleScopePicker
            value={overrideRoles}
            onChange={writeOverrideRoles}
            roles={roles}
            radioName="override-who"
            labelledBy="override-who-label"
            unrestrictedLabel="Operators with the step's override permission"
            selectTestId="override-roles-select"
            selectAriaLabel="Roles that may override"
          />

          <span className={styles.fieldLabel}>Require a reason</span>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              data-testid="override-require-reason"
              disabled={behavior === 'block'}
              checked={Boolean(automation.overrideConfig?.requireReason)}
              onChange={(e) =>
                onChange({
                  ...automation,
                  overrideConfig: { ...(automation.overrideConfig ?? {}), requireReason: e.target.checked },
                })
              }
            />
            Require a reason
          </label>

          <span className={styles.fieldLabel} id="on-override-label">
            On override
          </span>
          {/* Builder follow-ups (item f) */}
          <div className={styles.radioGroup} role="radiogroup" aria-labelledby="on-override-label">
            {ON_OVERRIDE_OPTIONS.map((o) => (
              <label key={o.value} className={styles.checkboxLabel}>
                <input
                  type="radio"
                  name="on-override"
                  checked={behavior === o.value}
                  onChange={() => onChange({ ...automation, overrideBehavior: o.value })}
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
